import { MRT_ColumnDef } from 'mantine-react-table';
import { Box, Button, Loader, Popover, Stack, Text, Tooltip } from '@mantine/core';
import type { CSSProperties } from 'react';
import { EntityParams } from 'app/models/entity-params';
import { toTitleCase } from 'app/utils/util-functions';
import { useDispatch, useSelector } from 'react-redux';
import { getSignableDocumentsGenerating } from 'app/state/ducks/signable-document/selectors';
import { settleSubTrade, unsettleSubTrade } from 'app/state/ducks/sub-trades/thunks';
import { StableKey } from 'app/models/common-types';
import { useState } from 'react';
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification';
import IonIcon from '@reacticons/ionicons';
import 'app/styles/components/data-trade-entry.css';
import DeleteConfirmation from 'app/views/components/delete-confirmation/DeleteConfirmation';
import TerminateSubTradeModal from 'app/views/pages/trade-entry/detail-trade/sub-trades/terminate-sub-trade-modal';
import { DeliveryView } from './delivery-view';
import HoldIndicator, { holdsToEntries, isLiftable, CARD_BODY_BG, FieldBlock, LOCK_RED, TRADE_CARD_WIDTH } from 'app/views/components/hold-indicator';
import { Hold } from 'app/models/hold-params';
import { PacketType } from 'app/state/ducks/trade-document-delivery/types';
import { DocIconStatus, docIconStatusColor, docIconStatusPrefix } from './doc-icon-status';
import { NotRefreshedDetail } from 'app/views/pages/trade-entry/utils/aa-document-not-refreshed';
import { formatNumberToCurrency } from 'app/utils/util-functions';
import moment from 'moment';
import { useTradeRefresh } from 'app/views/pages/trade-entry/detail-trade/use-trade-refresh';

// One view per packet record on the sub-trade (ADR_LOS-026): the Open and
// Settled packets share the Delivery column, distinguished by packetLabel.
export type DeliveryViewWithPacket = DeliveryView & { packetType: PacketType }


export type DocumentType = 'ConfirmationDocument' | 'AssignmentAgreementDocument' | 'FundingMemoDocument' | 'AffiliateLetterDocument'

export type SubTradeDocumentTableParams = {
    id: string;
    masterTradeId: StableKey;
    status: string;
    systemId?: string;
    clientId: string;
    myEntity: string;
    buyerEntity?: EntityParams | null;
    sellerEntity?: EntityParams | null;
    agentEntity?: EntityParams | null;
    targetSettlementDate: string;
    cDocumentIcon: any;
    cDocumentDate?: string | null;
    cDocumentStatus?: DocIconStatus | null;
    aaDocumentIcon: any;
    aaDocumentDate?: string | null;
    aaDocumentStatus?: DocIconStatus | null;
    aaDocumentSigned: boolean;
    fmDocumentIcon: any;
    fmDocumentDate?: string | null;
    fmDocumentStatus?: DocIconStatus | null;
    fmIsLocked?: boolean;
    /** Formatted time the FM was locked, for the lock tooltip. */
    fmLockedAt?: string | null;
    holds?: Hold[];
    alDocumentIcon: any;
    alDocumentDate?: string | null;
    alDocumentStatus?: DocIconStatus | null;
    isLoadingAAMetadata?: boolean;
    lockedDate?: string;
    refreshingDocumentType?: DocumentType | null;
    cHasSignatures?: boolean;
    aaHasSignatures?: boolean;
    alHasSignatures?: boolean;
    fmHasSignatures?: boolean;
    /** Set when this sub-trade's AA document records an amount the trade has
     * moved past. Only the AA can be checked this way — C/FM/AL keep no amount
     * snapshot to compare against. */
    notRefreshed?: NotRefreshedDetail | null;
    settlementMethod?: string | null;
    paymentType?: string | null;
    allFacilitiesTerminated?: boolean;
    deliveryView: DeliveryViewWithPacket | null;
}

const getTextStyle = (color: string, weight = 600, textAlign?: 'start' | 'end' | 'center'): CSSProperties => ({
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '14px',
    fontWeight: weight,
    lineHeight: '19px',
    textAlign: textAlign ?? 'center',
    color,
    width: '100%',
});

function TableHead({ header, textAlign }: { header: string, textAlign?: 'start' | 'end' | 'center' }) {
    return (
        <Text style={getTextStyle('#784595', 400, textAlign)}>
            {header}
        </Text>
    );
}

// Header for a document column (C / AA / FM / AL). Subscribes to Redux directly
// (rather than receiving a prop) so it re-renders when document generation
// starts/stops — MainTable memoizes column defs on mount, so a prop would never
// update. Shows a small spinner next to the label while a save is generating.
function DocumentColumnHeader({ header }: { header: string }) {
    const isGenerating = useSelector(getSignableDocumentsGenerating);
    return (
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <Text style={{ ...getTextStyle('#784595', 400), width: 'auto' }}>{header}</Text>
            {isGenerating && <Loader size="xs" color="#476DB9" />}
        </div>
    );
}

export type RefreshDocumentHandler = (subTradeId: string, documentType: DocumentType, hasSignatures: boolean) => Promise<void>

interface RefreshDocumentIconProps {
    subTradeId: string;
    documentType: DocumentType;
    isRefreshing: boolean;
    hasSignatures: boolean;
    onRefresh?: RefreshDocumentHandler;
}
const TOOLTIP_STYLE = {
    backgroundColor: '#FFFFFF',
    color: '#413E3E',
    border: '2px solid #6B7FD7',
    borderRadius: '4px',
    padding: '12px 16px',
    fontSize: '14px',
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: 400,
    lineHeight: '20px',
}

// Wide enough for one line of timestamp plus the lock / refresh glyphs beside
// it; the status word sits on its own line above the icon.
const documentColumnWidth = 104

// A settled sub-trade's documents are final: no column offers a refresh.
const isSettled = (row: SubTradeDocumentTableParams): boolean =>
    (row.status ?? '').toLowerCase() === 'settled'

// One action slot under the settlement date: Settle / Unsettle / Terminate all
// use these metrics so the column width is driven by the longest label.
const actionButtonStyle: CSSProperties = {
    width: '76px',
    height: '26px',
    borderRadius: '5px',
    padding: 0,
    fontSize: '12px',
}

// Line under a document icon: the timestamp, plus the lock and refresh
// affordances. Always rendered (even empty) so the labels of all five document
// columns share one baseline (AC 9).
function DocumentStatusLine({
    text,
    color,
    locked,
    lockedAt,
    refresh,
}: {
    text?: string | null
    color?: string
    locked?: boolean
    lockedAt?: string | null
    refresh?: RefreshDocumentIconProps
}) {
    return (
        <div className="documentStatusLine">
            {text && (
                <span style={{ fontSize: 10, color: color ?? '#476DB9', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                    {text}
                </span>
            )}
            {locked && <LockedDocumentIcon lockedAt={lockedAt} />}
            {refresh && <RefreshDocumentIcon {...refresh} />}
        </div>
    )
}

// Slot above the icon: the document's status, or the Delivery column's packet
// label. Always rendered so every icon sits on the same baseline.
function DocumentCellTopSlot({ label, color }: { label?: string | null, color?: string }) {
    return <span className="documentCellTopSlot" style={color ? { color } : undefined}>{label}</span>
}

// Subtle lock marker for a locked Funding Memo — a small muted glyph beside the
// timestamp rather than a badge competing with the document icon.
function LockedDocumentIcon({ lockedAt }: { lockedAt?: string | null }) {
    return (
        <Tooltip
            label={lockedAt ? `FM document is locked: ${lockedAt}` : 'FM document is locked'}
            position="top"
            withArrow
            withinPortal
            styles={{
                tooltip: TOOLTIP_STYLE,
            }}
        >
            <span style={{ display: 'inline-flex', alignItems: 'center' }} aria-label="Locked">
                <IonIcon
                    name="lock-closed"
                    style={{ height: '11px', width: '11px', color: '#9B9B9B' }}
                />
            </span>
        </Tooltip>
    )
}

// DAML bumps the document's createdAt on every regeneration, so this is the
// last time a refresh actually landed.
const formatLastRefreshed = (value: string) => {
    const parsed = moment(value)
    return parsed.isValid() ? parsed.format('D MMM YYYY, HH:mm') : value
}

const formatTradeDate = (value: string) => {
    const parsed = moment(value)
    return parsed.isValid() ? parsed.format('D MMM YYYY') : value
}

const formatAmount = (amount: string, currency: string) => {
    const value = Number(amount)
    if (Number.isNaN(value)) return amount
    return formatNumberToCurrency(value, currency || 'USD', 2, 2)
}

// Warning triangle drawn to match the hold badges next to it — same 24px grid,
// same stroke weight and caps, same size and colour — so the Alert column reads
// as one set of glyphs rather than two icon libraries side by side. Inlined for
// the same reason the padlock and octagon are.
function TriangleAlertIcon({ size = 20 }: { size?: number }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
        >
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
        </svg>
    )
}

// Red triangle in the Alert column for a sub-trade whose Assignment Agreement
// still records an amount the trade has moved past — a refresh that did not
// happen. Sits beside the hold indicator rather than replacing it: a trade can
// be both locked and out of date.
//
// Built from the hold card's own tokens and FieldBlock, and opened the same way
// — click to open, stays until you click away — so the two badges in this cell
// behave identically. Hovering only changes the cursor.
function NotRefreshedIndicator({ detail }: { detail: NotRefreshedDetail }) {
    const [opened, setOpened] = useState(false)
    // Side chosen once when the card opens, then locked via flip:false so it
    // cannot jump above↔below while open.
    const [side, setSide] = useState<'top' | 'bottom'>('top')

    // Open below when the badge sits in the top half of the viewport, above when
    // in the bottom half — so the card grows into the roomier side.
    const openAt = (el: HTMLElement) => {
        const rect = el.getBoundingClientRect()
        setSide(rect.top < window.innerHeight / 2 ? 'bottom' : 'top')
        setOpened(true)
    }

    const toggle = (el: HTMLElement) => {
        if (opened) setOpened(false)
        else openAt(el)
    }

    const card = (
        <div style={{ borderRadius: 8, overflow: 'hidden' }}>
            <div
                style={{
                    backgroundColor: LOCK_RED,
                    color: '#fff',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }}
            >
                <TriangleAlertIcon size={18} />
                <Text weight={700} style={{ color: '#fff', fontSize: 16, letterSpacing: '0.5px' }}>
                    Document Not Refreshed
                </Text>
            </div>
            <Stack style={{ backgroundColor: CARD_BODY_BG, padding: '12px 16px', gap: 8 }}>
                {detail.facilities.map((facility, index) => (
                    <FieldBlock
                        key={`${facility.facilityName}-${index}`}
                        label={facility.facilityName || 'Amount'}
                        value={`Shows ${formatAmount(facility.documentAmount, facility.currency)}, trade is now ${formatAmount(facility.tradeAmount, facility.currency)}`}
                    />
                ))}
                {detail.tradeDate && (
                    <FieldBlock
                        label="Trade date"
                        value={`Shows ${formatTradeDate(detail.tradeDate.documentValue)}, trade is now ${formatTradeDate(detail.tradeDate.tradeValue)}`}
                    />
                )}
                {detail.lastRefreshedAt && (
                    <FieldBlock
                        label="Last refreshed"
                        value={formatLastRefreshed(detail.lastRefreshedAt)}
                        noWrap
                    />
                )}
                <Text color="#888" style={{ fontSize: 12, lineHeight: 1.4 }}>
                    Regenerate it before sending or signing.
                </Text>
            </Stack>
        </div>
    )

    return (
        <Popover
            opened={opened}
            onChange={setOpened}
            position={side}
            middlewares={{ flip: false, shift: true }}
            withArrow
            arrowSize={12}
            shadow="md"
            withinPortal
            closeOnClickOutside
            styles={{
                dropdown: {
                    padding: 0,
                    borderRadius: 8,
                    backgroundColor: CARD_BODY_BG,
                    minWidth: TRADE_CARD_WIDTH,
                    maxWidth: TRADE_CARD_WIDTH,
                    width: TRADE_CARD_WIDTH,
                },
                // Arrow matches the card edge it touches: red title bar when the
                // card is below the badge, gray body when above.
                arrow: {
                    backgroundColor: side === 'bottom' ? LOCK_RED : CARD_BODY_BG,
                    border: 0,
                },
            }}
        >
            <Popover.Target>
                <span
                    role="button"
                    aria-label="Assignment Agreement not refreshed — click for details"
                    tabIndex={0}
                    // Stop the click bubbling so opening the card never triggers
                    // the row's own click handling.
                    onClick={e => {
                        e.stopPropagation()
                        toggle(e.currentTarget)
                    }}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            e.stopPropagation()
                            toggle(e.currentTarget)
                        }
                    }}
                    style={{
                        color: LOCK_RED,
                        display: 'inline-flex',
                        alignItems: 'center',
                        lineHeight: 1,
                        cursor: 'pointer',
                    }}
                >
                    <TriangleAlertIcon size={20} />
                </span>
            </Popover.Target>
            <Popover.Dropdown>{card}</Popover.Dropdown>
        </Popover>
    )
}

// One document cell, shared by C / AA / FM / AL so the four columns cannot
// drift apart in layout. Status sits above the icon and the timestamp below —
// the same stack the Delivery column uses, and half the width of one long line.
function DocumentIconCell({
    icon,
    status,
    timestamp,
    refresh,
    locked,
    lockedAt,
    style,
}: {
    icon: any
    status?: DocIconStatus | null
    timestamp?: string | null
    refresh?: RefreshDocumentIconProps
    locked?: boolean
    lockedAt?: string | null
    style?: CSSProperties
}) {
    const color = status ? docIconStatusColor(status) : undefined
    return (
        <div className="documentIconCell" style={style}>
            <DocumentCellTopSlot label={status ? docIconStatusPrefix(status) : null} color={color} />
            {icon}
            <DocumentStatusLine
                // Refreshing is in-flight and carries no timestamp of its own.
                text={status === DocIconStatus.Refreshing ? null : timestamp}
                color={color}
                locked={locked}
                lockedAt={lockedAt}
                refresh={refresh}
            />
        </div>
    )
}

function DeliveryDocumentIcon({
    state,
    color,
}: {
    state: DeliveryView['state']
    color: string
}) {
    if (state === 'none') {
        return (
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
                <path
                    d="M32.5 17.2852V32.5C32.5 33.4946 32.1049 34.4484 31.4017 35.1517C30.6984 35.8549 29.7446 36.25 28.75 36.25H11.25C10.2554 36.25 9.30161 35.8549 8.59835 35.1517C7.89509 34.4484 7.5 33.4946 7.5 32.5V7.5C7.5 6.50544 7.89509 5.55161 8.59835 4.84835C9.30161 4.14509 10.2554 3.75 11.25 3.75H18.9648C19.6277 3.7501 20.2633 4.01341 20.732 4.48203L31.768 15.518C32.2366 15.9867 32.4999 16.6223 32.5 17.2852Z"
                    fill="none"
                    stroke="black"
                    strokeLinejoin="round"
                />
                <path
                    d="M20 4.375V13.75C20 14.413 20.2634 15.0489 20.7322 15.5178C21.2011 15.9866 21.837 16.25 22.5 16.25H31.875"
                    stroke="black"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M26 25.5C26 21.9103 23.0897 19 19.5 19C15.9103 19 13 21.9103 13 25.5C13 29.0897 15.9103 32 19.5 32C23.0897 32 26 29.0897 26 25.5ZM19.3547 28.355C19.3081 28.3088 19.271 28.2538 19.2456 28.1932C19.2202 28.1326 19.2071 28.0677 19.2068 28.002C19.2065 27.9363 19.2192 27.8712 19.2441 27.8105C19.269 27.7497 19.3056 27.6944 19.3519 27.6478L20.9869 26H16.8125C16.6799 26 16.5527 25.9473 16.4589 25.8536C16.3652 25.7598 16.3125 25.6326 16.3125 25.5C16.3125 25.3674 16.3652 25.2402 16.4589 25.1464C16.5527 25.0527 16.6799 25 16.8125 25H20.9869L19.3519 23.3522C19.3056 23.3055 19.269 23.2502 19.2441 23.1894C19.2193 23.1286 19.2066 23.0635 19.2069 22.9978C19.2072 22.9321 19.2204 22.8671 19.2458 22.8065C19.2712 22.7459 19.3083 22.6909 19.355 22.6447C19.4017 22.5984 19.457 22.5618 19.5178 22.537C19.5786 22.5121 19.6437 22.4994 19.7094 22.4997C19.8421 22.5003 19.9691 22.5536 20.0625 22.6478L22.5434 25.1478C22.6364 25.2415 22.6885 25.3681 22.6885 25.5C22.6885 25.6319 22.6364 25.7585 22.5434 25.8522L20.0625 28.3522C20.0162 28.3989 19.9612 28.436 19.9006 28.4615C19.84 28.4869 19.7749 28.5001 19.7092 28.5004C19.6434 28.5007 19.5783 28.4879 19.5175 28.463C19.4566 28.4381 19.4013 28.4014 19.3547 28.355Z"
                    fill={color}
                />
            </svg>
        )
    }

    return (
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
            <path
                d="M32.5 17.2852V32.5C32.5 33.4946 32.1049 34.4484 31.4017 35.1517C30.6984 35.8549 29.7446 36.25 28.75 36.25H11.25C10.2554 36.25 9.30161 35.8549 8.59835 35.1517C7.89509 34.4484 7.5 33.4946 7.5 32.5V7.5C7.5 6.50544 7.89509 5.55161 8.59835 4.84835C9.30161 4.14509 10.2554 3.75 11.25 3.75H18.9648C19.6277 3.7501 20.2633 4.01341 20.732 4.48203L31.768 15.518C32.2366 15.9867 32.4999 16.6223 32.5 17.2852Z"
                fill="none"
                stroke="black"
                strokeLinejoin="round"
            />
            <path
                d="M26 25.5C26 21.9103 23.0897 19 19.5 19C15.9103 19 13 21.9103 13 25.5C13 29.0897 15.9103 32 19.5 32C23.0897 32 26 29.0897 26 25.5ZM19.3547 28.355C19.3081 28.3088 19.271 28.2538 19.2456 28.1932C19.2202 28.1326 19.2071 28.0677 19.2068 28.002C19.2065 27.9363 19.2192 27.8712 19.2441 27.8105C19.269 27.7497 19.3056 27.6944 19.3519 27.6478L20.9869 26H16.8125C16.6799 26 16.5527 25.9473 16.4589 25.8536C16.3652 25.7598 16.3125 25.6326 16.3125 25.5C16.3125 25.3674 16.3652 25.2402 16.4589 25.1464C16.5527 25.0527 16.6799 25 16.8125 25H20.9869L19.3519 23.3522C19.3056 23.3055 19.269 23.2502 19.2441 23.1894C19.2193 23.1286 19.2066 23.0635 19.2069 22.9978C19.2072 22.9321 19.2204 22.8671 19.2458 22.8065C19.2712 22.7459 19.3083 22.6909 19.355 22.6447C19.4017 22.5984 19.457 22.5618 19.5178 22.537C19.5786 22.5121 19.6437 22.4994 19.7094 22.4997C19.8421 22.5003 19.9691 22.5536 20.0625 22.6478L22.5434 25.1478C22.6364 25.2415 22.6885 25.3681 22.6885 25.5C22.6885 25.6319 22.6364 25.7585 22.5434 25.8522L20.0625 28.3522C20.0162 28.3989 19.9612 28.436 19.9006 28.4615C19.84 28.4869 19.7749 28.5001 19.7092 28.5004C19.6434 28.5007 19.5783 28.4879 19.5175 28.463C19.4566 28.4381 19.4013 28.4014 19.3547 28.355Z"
                fill={color}
            />
            <path
                d="M20 4.375V13.75C20 14.413 20.2634 15.0489 20.7322 15.5178C21.2011 15.9866 21.837 16.25 22.5 16.25H31.875"
                stroke="black"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

// Small refresh affordance next to the timestamp (AC 7): hidden until the row
// is hovered (opacity transition in CSS), and always visible + spinning while
// that document is refreshing.
function RefreshDocumentIcon({ subTradeId, documentType, isRefreshing, hasSignatures, onRefresh }: RefreshDocumentIconProps) {
    return (
        <Tooltip
            label="Click to regenerate and update the docs"
            position="top"
            withArrow
            withinPortal
            styles={{
                tooltip: TOOLTIP_STYLE,
            }}
        >
            <Box
                component="button"
                type="button"
                aria-label="Regenerate document"
                onClick={isRefreshing ? undefined : () => onRefresh?.(subTradeId, documentType, hasSignatures)}
                disabled={isRefreshing}
                className={`refreshDocumentIconButton${isRefreshing ? ' isRefreshing' : ''}`}
                sx={{ cursor: isRefreshing ? 'default' : 'pointer' }}
            >
                <IonIcon
                    name="refresh-outline"
                    style={{
                        height: '12px',
                        width: '12px',
                        color: '#32439A',
                        pointerEvents: 'none',
                    }}
                />
            </Box>
        </Tooltip>
    );
}

export const getColumnDefs = (
    myEntity?: string,
    onRefreshDocument?: RefreshDocumentHandler,
    onLiftTrade?: (subTradeId: string, hold: Hold) => void,
    openConfirm?: (
        subTradeId: string,
        packetType?: PacketType,
        mode?: 'send' | 'resend' | 'retryPrep' | 'retrySend',
    ) => void,
): MRT_ColumnDef<SubTradeDocumentTableParams>[] => [
        {
            size: 45,
            accessorKey: 'status',
            header: 'Status',
            mantineTableHeadCellProps: {
                className: 'cell-width-100',
            },
            Header: <div style={{ width: "100%" }}><TableHead header={'Status'} /></div>,
            Cell: ({ cell }) => {
                const status = (cell.getValue() as string ?? '').toLowerCase();
                const statusColors: { [key: string]: string } = {
                    open: '#287A26',
                    settled: '#EE5959',
                    draft: '#757373',
                    canceled: '#EE5959',
                    terminated: '#E8006E',
                };

                return (
                    <Text style={getTextStyle(statusColors[status] || '#000000')}>
                        {toTitleCase(cell.getValue() as string ?? '')}
                    </Text>
                );
            },
        },
        {
            size: 75,
            header: 'Trade Ids',
            mantineTableHeadCellProps: {
                className: 'cell-width-100',
            },
            Header: () => (
                <div style={{ width: '100%' }}>
                    <TableHead header={'SubTrade ID'} textAlign='start' />
                    <TableHead header={'Internal ID'} textAlign='start' />
                </div>
            ),
            Cell: ({ row }) => {
                const subTradeId = row.original.systemId ?? row.original.id;
                const clientId = row.original.clientId;
                return (
                    <div>
                        <Text style={{ ...getTextStyle('#476DB9'), textAlign: 'start' }}>
                            {subTradeId}
                        </Text>
                        <Text style={{ ...getTextStyle('#413E3E'), textAlign: 'start' }}>
                            {clientId}
                        </Text>
                    </div>
                );
            },
        },
        {
            size: 55,
            header: 'My Entity',
            mantineTableHeadCellProps: {
                className: 'cell-width-100',
            },
            Header: () => {
                const myEntityType = myEntity === 'Buy' ? 'Buyer' : 'Seller';

                return (
                    <div style={{ width: '100%' }}>
                        <TableHead header={`My Entity (${myEntityType})`} textAlign='start' />
                        <TableHead header={'MEI'} textAlign='start' />
                    </div>
                );
            },
            Cell: ({ row }) => {
                const entity = row.original.myEntity === 'Buy' ? row.original.buyerEntity : row.original.sellerEntity;
                const entityName = entity?.entityName ?? 'Unknown';
                const entityMei = entity?.mei ?? 'N/A';

                return (
                    <div>
                        <Text style={{ ...getTextStyle('#476DB9'), textAlign: 'start' }}>
                            {entityName}
                        </Text>
                        <Text style={{ ...getTextStyle('#413E3E'), textAlign: 'start' }}>{entityMei}</Text>
                    </div>
                );
            },
        },
        {
            size: 55,
            header: 'Counterparty',
            mantineTableHeadCellProps: {
                className: 'cell-width-100',
            },
            Header: () => {
                const counterpartyType = myEntity === 'Buy' ? 'Seller' : 'Buyer';

                return (
                    <div style={{ width: '100%' }}>
                        <TableHead header={`Counterparty (${counterpartyType})`} textAlign='start' />
                        <TableHead header={'MEI'} textAlign='start' />
                    </div>
                );
            },
            Cell: ({ row }) => {
                const entity = row.original.myEntity === 'Sell' ? row.original.buyerEntity : row.original.sellerEntity;
                const entityName = entity?.entityName ?? 'Unknown';
                const entityMei = entity?.mei ?? 'N/A';

                return (
                    <div>
                        <Text style={{ ...getTextStyle('#476DB9'), textAlign: 'start' }}>
                            {entityName}
                        </Text>
                        <Text style={{ ...getTextStyle('#413E3E'), textAlign: 'start' }}>{entityMei}</Text>
                    </div>
                );
            },
        },

        {
            size: 55,
            header: 'agentEntity',
            mantineTableHeadCellProps: {
                className: 'cell-width-100',
            },
            Header: () => (
                <div style={{ width: '100%' }}>
                    <TableHead header={'Agent'} textAlign='start' />
                    <TableHead header={'MEI'} textAlign='start' />
                </div>
            ),
            Cell: ({ row }) => {
                const entityName = row.original.agentEntity?.entityName ?? 'Unknown';
                const entityMei = row.original.agentEntity?.mei ?? 'N/A';
                return (
                    <div>
                        <Text style={{ ...getTextStyle('#476DB9'), textAlign: 'start' }}>
                            {entityName}
                        </Text>
                        <Text style={{ ...getTextStyle('#413E3E'), textAlign: 'start' }}>{entityMei}</Text>
                    </div>
                );
            },
        },
        {
            size: 40,
            header: 'Alert',
            mantineTableHeadCellProps: {
                className: 'cell-width-100',
            },
            Header: () => (
                <div style={{ width: '100%' }}>
                    <TableHead header={'Alert'} />
                </div>
            ),
            Cell: ({ row }) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}>
                        <HoldIndicator
                            holds={holdsToEntries(
                                row.original.holds ?? [],
                                'Trade',
                                (h) =>
                                    onLiftTrade && isLiftable(h, 'Trade')
                                        ? () => onLiftTrade(row.original.id, h)
                                        : undefined,
                            )}
                            variant="auto"
                            interaction="click"
                            layout="accordion"
                            ariaLabel="Sub-trade lock status"
                        />
                        {row.original.notRefreshed && (
                            <NotRefreshedIndicator detail={row.original.notRefreshed} />
                        )}
                    </div>
                );
            },
        },
        {
            // Settlement date and the sub-trade's action share one column: a
            // trade is either settled or terminated, never both, so a single
            // button sits under the date. Settle needs a settlement date to act
            // on; Terminate does not, and takes precedence when it applies.
            size: 90,
            header: 'Settlement Date',
            mantineTableHeadCellProps: {
                className: 'cell-width-100',
            },
            Header: () => (
                <div style={{ width: '100%' }}>
                    <TableHead header={'Settlement Date'} />
                </div>
            ),
            Cell: ({ row }) => {
                const dispatch = useDispatch();
                const { refreshTrade } = useTradeRefresh();
                const status = row.original.status.toLowerCase();
                const lockedDate = row.original.lockedDate;
                const settlementDate = row.original.targetSettlementDate;
                const isLoading = row.original.isLoadingAAMetadata;
                const aaDocumentSigned = row.original.aaDocumentSigned;
                const subTradeId = row.original.systemId ?? row.original.id;
                const isSettleEligible = status === 'open' && aaDocumentSigned;
                const isUnsettleEligible = status === 'settled';
                const disabled = !isSettleEligible && !isUnsettleEligible;
                const [loading, setLoading] = useState(false);
                const [deleteModalOpen, setDeleteModalOpen] = useState(false);
                const [terminateModalOpen, setTerminateModalOpen] = useState(false);
                const canTerminate = status === 'open' && row.original.allFacilitiesTerminated === true;

                // Show date only if: Open status + has settlement date + AA is fully signed
                const shouldShowDate = lockedDate || (status === 'open' && settlementDate && aaDocumentSigned);

                const handleSettle = async () => {
                    setLoading(true);
                    try {
                        await dispatch(settleSubTrade(
                            { subTradeId: { id: row.original.id, admin: row.original.masterTradeId.admin }, masterTradeId: row.original.masterTradeId },
                            // the tab's re-read below brings the funding memos
                            { reloadLockedFMMetadatas: false },
                        ));
                        // this trade's reads, not every master trade (ADR_LOS-030 step 3);
                        // awaited so the row cannot show the old Open state, nor take
                        // a second settle, before the re-read lands
                        await refreshTrade();
                    } catch (error: unknown) {
                        ErrorNotification({
                            title: 'Error Settling SubTrade',
                            message: (error as Error).message
                        })
                    }
                    setLoading(false);
                }

                const handleUnSettle = async () => {
                    setLoading(true);
                    try {
                        const unsettleResult: any = await dispatch(unsettleSubTrade(
                            { subTradeId: { id: row.original.id, admin: row.original.masterTradeId.admin }, masterTradeId: row.original.masterTradeId },
                            // the tab's re-read below brings the funding memos
                            { reloadLockedFMMetadatas: false },
                        ));
                        await refreshTrade();
                        if (!unsettleResult.success) {
                            ErrorNotification({
                                title: 'Error Unsettling SubTrade',
                                message: unsettleResult.payload || 'An unknown error occurred while unsettling the sub-trade.'
                            })
                        }
                        if (unsettleResult.success) {
                            SuccessNotification({
                                message: 'Sub-trade has been successfully unsettled.',
                                title: 'Unsettle Successful',
                            })
                        }
                    } catch (error: unknown) {
                        ErrorNotification({
                            title: 'Error Unsettling SubTrade',
                            message: (error as Error).message
                        })
                    }
                    setLoading(false);
                    setDeleteModalOpen(false);
                }

                const openUnsettleModal = () => {
                    setDeleteModalOpen(true);
                }

                // No settlement date to act on (or still loading) means no settle
                // action either — the button would have been disabled in every
                // such case. Terminate is not tied to the date. A terminated
                // sub-trade is finished: it offers no action at all.
                const isTerminated = status === 'terminated'
                const showSettleAction = !canTerminate && !isTerminated && !isLoading && !!shouldShowDate

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: '100%' }}>
                        <Text style={getTextStyle('#476DB9')}>
                            {isLoading ? '...' : (shouldShowDate ? settlementDate || lockedDate : '...')}
                        </Text>

                        {canTerminate && (
                            <Button
                                style={{ ...actionButtonStyle, backgroundColor: '#475569', color: '#FFFFFF', border: 'none' }}
                                onClick={() => setTerminateModalOpen(true)}
                            >Terminate</Button>
                        )}

                        {showSettleAction && (
                            <Button
                                style={{
                                    ...actionButtonStyle,
                                    backgroundColor: disabled ? '#FFFFFF' : '#32439A',
                                    color: disabled ? '#000000' : '#FFFFFF',
                                    border: disabled ? '1px solid #B1B1B1' : 'none',
                                    boxShadow: disabled ? '0 4px 4px 0 rgba(0, 0, 0, 0.25)' : 'none'
                                }}
                                disabled={loading || disabled}
                                onClick={status === 'settled' ? openUnsettleModal : handleSettle}
                            >{status === 'settled' ? 'Unsettle' : 'Settle'}</Button>
                        )}

                        {deleteModalOpen && (
                            <DeleteConfirmation
                                onConfirm={handleUnSettle}
                                onCancel={() => setDeleteModalOpen(false)}
                                message={`Are you sure you want to unsettle sub-trade ${subTradeId}?`}
                                title={`Unsettle Sub-Trade ${subTradeId}`}
                                confirmText='Unsettle'
                                isLoading={loading}
                            />
                        )}

                        {terminateModalOpen && (
                            <TerminateSubTradeModal
                                opened={terminateModalOpen}
                                close={() => setTerminateModalOpen(false)}
                                subTradeId={row.original.id}
                                subTradeKey={{ id: row.original.id, admin: row.original.masterTradeId.admin }}
                                masterTradeId={row.original.masterTradeId}
                                subTradeDisplayId={subTradeId}
                            />
                        )}
                    </div>

                );
            },
        },
        {
            size: documentColumnWidth,
            header: 'C',
            mantineTableHeadCellProps: {
                className: 'cell-width-100',
                sx: { borderLeft: "1px solid #CAC9C9 !important", background: "#F0EEEE" },
            },
            mantineTableBodyCellProps: () => ({
                sx: { borderLeft: "1px solid #CAC9C9 !important", position: 'relative' },
            }),
            Header: () => <DocumentColumnHeader header={'C'} />,
            Cell: ({ row }) => {
                const canRefresh = row.original.settlementMethod === 'LendOS' && row.original.paymentType !== 'Elevation' && !isSettled(row.original);
                const isRefreshing = row.original.refreshingDocumentType === 'ConfirmationDocument'
                return (
                    <DocumentIconCell
                        icon={row.original.cDocumentIcon}
                        status={isRefreshing ? DocIconStatus.Refreshing : row.original.cDocumentStatus}
                        timestamp={row.original.cDocumentDate}
                        refresh={canRefresh ? {
                            subTradeId: row.original.id,
                            documentType: 'ConfirmationDocument',
                            isRefreshing,
                            hasSignatures: row.original.cHasSignatures ?? false,
                            onRefresh: onRefreshDocument,
                        } : undefined}
                    />
                )
            },
        },
        {
            size: documentColumnWidth,
            header: 'AA',
            mantineTableHeadCellProps: {
                className: 'cell-width-100',
                sx: { borderLeft: "1px solid #CAC9C9 !important", background: "#F0EEEE" },
            },
            mantineTableBodyCellProps: () => ({
                sx: { borderLeft: "1px solid #CAC9C9 !important", position: 'relative' },
            }),
            Header: () => <DocumentColumnHeader header={'AA'} />,
            Cell: ({ row }) => {
                const canRefresh = row.original.settlementMethod === 'LendOS' && !isSettled(row.original);
                const isRefreshing = row.original.refreshingDocumentType === 'AssignmentAgreementDocument'
                return (
                    <DocumentIconCell
                        icon={row.original.aaDocumentIcon}
                        status={isRefreshing ? DocIconStatus.Refreshing : row.original.aaDocumentStatus}
                        timestamp={row.original.aaDocumentDate}
                        refresh={canRefresh ? {
                            subTradeId: row.original.id,
                            documentType: 'AssignmentAgreementDocument',
                            isRefreshing,
                            hasSignatures: row.original.aaHasSignatures ?? false,
                            onRefresh: onRefreshDocument,
                        } : undefined}
                    />
                )
            },
        },
        {
            size: documentColumnWidth,
            header: 'FM',
            mantineTableHeadCellProps: {
                className: 'cell-width-100',
                sx: { borderLeft: "1px solid #CAC9C9 !important", background: "#F0EEEE" },
            },
            mantineTableBodyCellProps: () => ({
                sx: { borderLeft: "1px solid #CAC9C9 !important", position: 'relative' },
            }),
            Header: () => <DocumentColumnHeader header={'FM'} />,
            Cell: ({ row }) => {
                const hasDocument = row.original.fmDocumentIcon !== null
                const isLocked = row.original.fmIsLocked
                const isRefreshing = row.original.refreshingDocumentType === 'FundingMemoDocument'
                // Same gate as the other columns, plus one of its own: the FM
                // must not be locked. A locked FM is a frozen snapshot -
                // regenerating it would only reproduce the same PDF, since
                // generateFundingMemoInDocuSeal reads the locked data - so the
                // action is withheld rather than offered as a no-op. Unlock to
                // refresh. The lock glyph keeps that slot in the status line.
                // An empty cell still offers the action: the refresh path no
                // longer needs an existing SignableDocument, so it creates the
                // missing FundingMemoDocument.
                const canRefresh =
                    !isLocked &&
                    row.original.settlementMethod === 'LendOS' &&
                    !isSettled(row.original)
                return (
                    <DocumentIconCell
                        icon={row.original.fmDocumentIcon}
                        status={isRefreshing ? DocIconStatus.Refreshing : row.original.fmDocumentStatus}
                        timestamp={row.original.fmDocumentDate}
                        locked={hasDocument && isLocked}
                        lockedAt={row.original.fmLockedAt}
                        refresh={canRefresh ? {
                            subTradeId: row.original.id,
                            documentType: 'FundingMemoDocument',
                            isRefreshing,
                            hasSignatures: row.original.fmHasSignatures ?? false,
                            onRefresh: onRefreshDocument,
                        } : undefined}
                    />
                )
            },
        },
        {
            size: documentColumnWidth,
            header: 'AL',
            mantineTableHeadCellProps: {
                className: 'cell-width-100',
                sx: { borderLeft: "1px solid #CAC9C9 !important", background: "#F0EEEE" },
            },
            mantineTableBodyCellProps: () => ({
                sx: { borderLeft: "1px solid #CAC9C9 !important", position: 'relative' },
            }),
            Header: () => <DocumentColumnHeader header={'AL'} />,
            Cell: ({ row }) => {
                const canRefresh = row.original.settlementMethod === 'LendOS' && !isSettled(row.original);
                const isRefreshing = row.original.refreshingDocumentType === 'AffiliateLetterDocument'
                return (
                    <DocumentIconCell
                        icon={row.original.alDocumentIcon}
                        status={isRefreshing ? DocIconStatus.Refreshing : row.original.alDocumentStatus}
                        timestamp={row.original.alDocumentDate}
                        refresh={canRefresh ? {
                            subTradeId: row.original.id,
                            documentType: 'AffiliateLetterDocument',
                            isRefreshing,
                            hasSignatures: row.original.alHasSignatures ?? false,
                            onRefresh: onRefreshDocument,
                        } : undefined}
                    />
                )
            },
        },
        {
            size: documentColumnWidth,
            header: 'Delivery',
            mantineTableHeadCellProps: {
                className: 'cell-width-100',
                sx: { borderLeft: "1px solid #CAC9C9 !important", background: "#F0EEEE" },
            },
            mantineTableBodyCellProps: () => ({
                sx: { borderLeft: "1px solid #CAC9C9 !important", position: 'relative' },
            }),
            Header: () => (<div style={{ width: '100%' }}><TableHead header={'Delivery'} /></div>),
            Cell: ({ row }) => {
                const v = row.original.deliveryView
                // No packet: gray dash, no labels (LOS-6471 AC 1).
                if (!v) {
                    return (
                        <div className="documentIconCell">
                            <DocumentCellTopSlot />
                            <DeliveryDocumentIcon state="none" color="#9B9B9B" />
                            <div className="documentStatusLine">
                                <span
                                    aria-hidden="true"
                                    style={{
                                        display: 'block',
                                        width: 10,
                                        borderTop: '1px solid #6B6B6B',
                                    }}
                                />
                            </div>
                        </div>
                    )
                }
                // Popup mode by state (LOS-6471): prepared -> confirm send;
                // stale -> re-send advisory; failed -> retry.
                const mode =
                    v.state === 'stale' ? 'resend'
                        : v.state === 'prepFailed' ? 'retryPrep'
                            : v.state === 'sendFailed' ? 'retrySend'
                                : 'send'
                const clickTitle =
                    v.state === 'prepared' ? 'Click to confirm send'
                        : v.state === 'stale' ? 'AA economics changed — click to re-send'
                            : 'Click to retry'
                return (
                    <div className="documentIconCell">
                        <DocumentCellTopSlot label={v.packetLabel} color={'#476DB9'} />
                        <button
                            type="button"
                            style={{
                                position: 'relative',
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                cursor: v.clickable ? 'pointer' : 'default',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            disabled={!v.clickable}
                            onClick={v.clickable ? () => openConfirm?.(row.original.id, v.packetType, mode) : undefined}
                            data-test-id={`delivery-icon-${v.packetType}-${v.state}`}
                            title={v.clickable ? clickTitle : undefined}
                            // The icon svg and badge are decorative (aria-hidden) and the
                            // status text is a sibling — the button needs its own name.
                            aria-label={[v.packetLabel, v.label ?? v.state, v.clickable ? clickTitle : null]
                                .filter(Boolean)
                                .join(' — ')}
                        >
                            <DeliveryDocumentIcon
                                state={v.state === 'none' ? 'none' : v.state === 'sent' ? 'sent' : 'prepared'}
                                color={v.color}
                            />
                            {v.badge && (
                                <span
                                    aria-hidden="true"
                                    style={{
                                        position: 'absolute',
                                        top: -4,
                                        right: -6,
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        backgroundColor: v.color,
                                        color: '#FFFFFF',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        lineHeight: '16px',
                                        textAlign: 'center',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {v.badge}
                                </span>
                            )}
                        </button>
                        <div className="documentStatusLine">
                            {v.label && (
                                <span style={{ fontSize: 10, color: '#476DB9', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                                    {v.label}
                                </span>
                            )}
                        </div>
                    </div>
                )
            },
        },
    ];
